import React, { useContext, useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import AnimatedLottieView from 'lottie-react-native';
import { useNavigation } from '@react-navigation/native';
import { ExtrasContext } from '../Context/ExtrasContext';
import { AuthContext } from '../Context/AuthContext';
import { useTheme, GTFonts, PoppinsFonts, LatoFonts } from '../theme';
import { s, fs, iconSize, v, borderWidth } from '../utils/libs';
import { isTablet } from 'react-native-device-info';
import Button from '../components/Reusables/Button';
import Typography from '../components/Reusables/Typography';
import NetInfo from '@react-native-community/netinfo';
import MaterialIcons from '@react-native-vector-icons/material-icons/static';
import SafeAreaWrapper from '../components/Reusables/SafeAreaWrapper';

const isWeb = Platform.OS === 'web';

const NoInternetScreen: React.FC = () => {
  const navigation = useNavigation();
  const { theme, isDark } = useTheme();
  const {
    networkAvailableGlobal,
    setNetworkAvailableGlobal,
    setContinueOffline,
  } = useContext(ExtrasContext);
  const { auth } = useContext(AuthContext);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const isAuthenticated = !!(auth?.accessToken?.length > 0);

  const styles = createStyles(theme);

  const checkConnection = async () => {
    setIsCheckingConnection(true);
    try {
      const netInfoState = await NetInfo.refresh();
      const isOnline =
        netInfoState.isConnected && (netInfoState.isInternetReachable ?? false);

      if (isOnline) {
        setNetworkAvailableGlobal(true);
        // Small delay to show success feedback
        setTimeout(() => {
          navigation.goBack();
        }, 1000);
      } else {
        // Show feedback that connection check failed
        setNetworkAvailableGlobal(false);
      }
    } catch (error) {
      console.error('Error checking network:', error);
      setNetworkAvailableGlobal(false);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  // Auto-check connection when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isCheckingConnection) {
        checkConnection();
      }
    }, 2000); // Auto-check after 2 seconds

    return () => clearTimeout(timer);
  }, []);

  // Block leaving this screen if unauthenticated AND still offline.
  // When back online, allow dismissal.
  useEffect(() => {
    if (isAuthenticated || networkAvailableGlobal) return;
    const unsubscribe = (navigation as any).addListener(
      'beforeRemove',
      (e: any) => {
        e.preventDefault();
      },
    );
    (navigation as any).setOptions?.({ gestureEnabled: false });
    return () => {
      unsubscribe?.();
      (navigation as any).setOptions?.({ gestureEnabled: true });
    };
  }, [navigation, isAuthenticated, networkAvailableGlobal]);

  // Auto-dismiss as soon as global network becomes available
  useEffect(() => {
    if (networkAvailableGlobal) {
      (navigation as any).setOptions?.({ gestureEnabled: true });
      if ((navigation as any).canGoBack?.()) {
        navigation.goBack();
      }
    }
  }, [networkAvailableGlobal, navigation]);

  // Web-specific layout for better UX on larger screens
  if (isWeb) {
    return (
      <View style={styles.webContainer}>
        <View style={styles.webCard}>
          {/* Animation Section */}
          <View style={styles.webAnimationContainer}>
            <AnimatedLottieView
              source={require('../assets/lottie/noInternet.json')}
              autoPlay
              loop
              resizeMode="contain"
              style={styles.webAnimation}
            />
          </View>

          {/* Content Section */}
          <View style={styles.webContentSection}>
            <View style={styles.webIconContainer}>
              <MaterialIcons
                name="wifi-off"
                size={iconSize(32)}
                color={theme.colors.error}
              />
            </View>

            <Typography style={styles.webTitle}>
              No Internet Connection
            </Typography>

            <Typography style={styles.webDescription}>
              {isAuthenticated
                ? 'You can continue using some features offline, but certain actions require an internet connection.'
                : 'Please check your connection and try again.'}
            </Typography>

            {/* Action Buttons */}
            <View style={styles.webButtonContainer}>
              <TouchableOpacity
                style={[
                  styles.webPrimaryButton,
                  isCheckingConnection && styles.webButtonDisabled,
                ]}
                onPress={checkConnection}
                disabled={isCheckingConnection}
                activeOpacity={0.8}>
                {isCheckingConnection ? (
                  <Typography style={styles.webPrimaryButtonText}>
                    Checking...
                  </Typography>
                ) : (
                  <>
                    <MaterialIcons
                      name="refresh"
                      size={iconSize(20)}
                      color={theme.colors.textInverse}
                    />
                    <Typography style={styles.webPrimaryButtonText}>
                      Try Again
                    </Typography>
                  </>
                )}
              </TouchableOpacity>

              {isAuthenticated && (
                <TouchableOpacity
                  style={styles.webSecondaryButton}
                  onPress={() => {
                    setContinueOffline(true);
                    navigation.goBack();
                  }}
                  disabled={isCheckingConnection}
                  activeOpacity={0.7}>
                  <Typography
                    style={[
                      styles.webSecondaryButtonText,
                      isCheckingConnection && styles.webTextDisabled,
                    ]}>
                    Continue Offline
                  </Typography>
                </TouchableOpacity>
              )}
            </View>

            {/* Tips Section */}
            <View style={styles.webTipsContainer}>
              <View style={styles.webTipItem}>
                <MaterialIcons
                  name="wifi"
                  size={iconSize(18)}
                  color={theme.colors.textSecondary}
                />
                <Typography style={styles.webTipText}>
                  Check your Wi-Fi or ethernet connection
                </Typography>
              </View>
              <View style={styles.webTipItem}>
                <MaterialIcons
                  name="router"
                  size={iconSize(18)}
                  color={theme.colors.textSecondary}
                />
                <Typography style={styles.webTipText}>
                  Try restarting your router or modem
                </Typography>
              </View>
              <View style={styles.webTipItem}>
                <MaterialIcons
                  name="public"
                  size={iconSize(18)}
                  color={theme.colors.textSecondary}
                />
                <Typography style={styles.webTipText}>
                  Contact your network administrator
                </Typography>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  }

  // Native layout (unchanged)
  return (
    <SafeAreaWrapper
      edges={['right', 'bottom', 'left']}
      statusBarStyle={isDark ? 'light-content' : 'dark-content'}
      backgroundColor={theme.colors.background}
      style={styles.container}>
      <ScrollView
        style={styles.contentContainer}
        contentContainerStyle={{
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <View style={styles.textContainer}>
          <View style={{ alignItems: 'center' }}>
            <View style={styles.badgeRow}>
              <MaterialIcons
                name="wifi-off"
                size={iconSize(isTablet() ? 22 : 18)}
                color={theme.colors.error}
              />
            </View>
            <Typography style={styles.title}>No Internet Connection</Typography>
          </View>

          <Typography style={styles.description}>
            {isAuthenticated
              ? 'You can continue using some features offline, but certain actions require an internet connection.'
              : 'Dear User, Please connect to the internet to continue using the app.'}
          </Typography>
        </View>
        <View style={styles.animationContainer}>
          <AnimatedLottieView
            source={require('../assets/lottie/noInternet.json')}
            autoPlay
            loop
            resizeMode="contain"
            style={styles.animation}
          />
        </View>
        <View style={{ marginTop: v(30) }}>
          <View style={styles.buttonContainer}>
            <Button
              text={isCheckingConnection ? 'Checking...' : 'Refresh'}
              onPress={checkConnection}
              disabled={isCheckingConnection}
              backgroundColor={theme.colors.primary}
              rippleColor={theme.colors.primaryDark}
              width={s(200)}
              height={v(44)}
              borderRadius={s(8)}
              fontSize={fs(16)}
              fontFamily={GTFonts.semibold}
              loading={isCheckingConnection}
            />
            {isAuthenticated ? (
              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={() => {
                  setContinueOffline(true);
                  navigation.goBack();
                }}
                disabled={isCheckingConnection}>
                <Typography
                  style={[
                    styles.secondaryButtonText,
                    isCheckingConnection && styles.disabledText,
                  ]}>
                  Continue Offline
                </Typography>
              </TouchableOpacity>
            ) : null}
          </View>
          <View style={styles.tipsContainer}>
            <Typography style={styles.tipsTitle}>Connection Tips:</Typography>
            <View style={styles.tipsList}>
              <Typography style={styles.tipText}>
                • Check your Wi-Fi or mobile data connection
              </Typography>
              <Typography style={styles.tipText}>
                • Move closer to your router if using Wi-Fi
              </Typography>
              <Typography style={styles.tipText}>
                • Restart your device if the problem persists
              </Typography>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaWrapper>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    // ============ NATIVE STYLES ============
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    contentContainer: {
      flex: 1,
      paddingHorizontal: s(20),
      paddingVertical: s(20),
    },
    badgeRow: {
      width: '100%',
      alignItems: 'center',
      marginBottom: v(8),
    },
    badge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      paddingVertical: s(6),
      paddingHorizontal: s(12),
      borderRadius: 999,
      backgroundColor: theme.colors.surfaceSecondary,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
    },
    badgeText: {
      color: theme.colors.text,
      fontFamily: PoppinsFonts.regular,
      fontSize: fs(isTablet() ? 14 : 12),
    },
    animationContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      maxHeight: v(200),
      width: '100%',
    },
    animation: {
      width: '100%',
      aspectRatio: 1.2,
      marginBottom: v(20),
    },
    textContainer: {
      alignItems: 'center',
      marginBottom: v(40),
      marginTop: v(30),
      maxWidth: s(300),
    },
    title: {
      fontSize: fs(isTablet() ? 28 : 24),
      color: theme.colors.text,
      textAlign: 'center',
      fontFamily: GTFonts.bold,
      marginBottom: v(16),
      lineHeight: fs(isTablet() ? 34 : 30),
    },
    subtitle: {
      fontSize: fs(isTablet() ? 18 : 16),
      color: theme.colors.textSecondary,
      textAlign: 'center',
      fontFamily: LatoFonts.regular,
      marginBottom: v(12),
      lineHeight: fs(isTablet() ? 24 : 22),
    },
    description: {
      fontSize: fs(isTablet() ? 16 : 14),
      color: theme.colors.textSecondary,
      textAlign: 'center',
      fontFamily: LatoFonts.regular,
      lineHeight: fs(isTablet() ? 22 : 20),
      opacity: 0.8,
    },
    buttonContainer: {
      alignItems: 'center',
      marginTop: v(10),
      marginBottom: v(40),
      gap: v(20),
    },
    secondaryButton: {
      paddingVertical: s(12),
      paddingHorizontal: s(24),
      borderRadius: s(20),
    },
    secondaryButtonText: {
      fontSize: fs(isTablet() ? 16 : 14),
      color: theme.colors.secondary,
      textAlign: 'center',
      fontFamily: GTFonts.regular,
    },
    disabledText: {
      opacity: 0.5,
    },
    tipsContainer: {
      marginTop: v(20),
      backgroundColor: theme.colors.surface,
      borderRadius: isTablet() ? theme.borderRadius.md : theme.borderRadius.sm,
      padding: s(20),
      maxWidth: s(320),
      shadowColor: theme.colors.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: s(8),
      elevation: 4,
    },
    tipsTitle: {
      fontSize: fs(isTablet() ? 18 : 16),
      lineHeight: fs(isTablet() ? 20 : 18),
      color: theme.colors.text,
      marginBottom: v(12),
      fontFamily: GTFonts.semibold,
    },
    tipsList: {
      gap: v(8),
    },
    tipText: {
      fontSize: fs(isTablet() ? 15 : 13),
      color: theme.colors.textSecondary,
      lineHeight: fs(isTablet() ? 20 : 18),
      fontFamily: LatoFonts.regular,
    },

    // ============ WEB STYLES ============
    webContainer: {
      flex: 1,
      backgroundColor: theme.colors.background,
      alignItems: 'center',
      justifyContent: 'center',
      padding: s(24),
      minHeight: '100%',
    },
    webCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: s(20),
      shadowColor: theme.colors.shadow,
      shadowOffset: { width: 0, height: s(8) },
      shadowOpacity: 0.08,
      shadowRadius: s(24),
      elevation: 8,
      maxWidth: s(480),
      width: '100%',
      overflow: 'hidden',
    },
    webAnimationContainer: {
      backgroundColor: theme.colors.surfaceSecondary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: v(48),
      borderBottomWidth: borderWidth(1),
      borderBottomColor: theme.colors.border,
    },
    webAnimation: {
      width: s(320),
      height: v(256),
    },
    webContentSection: {
      padding: s(32),
      alignItems: 'center',
    },
    webIconContainer: {
      width: s(64),
      height: s(64),
      borderRadius: s(32),
      backgroundColor: `${theme.colors.error}15`,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: v(20),
    },
    webTitle: {
      fontSize: fs(26),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: v(12),
      letterSpacing: -0.5,
    },
    webDescription: {
      fontSize: fs(15),
      fontFamily: LatoFonts.regular,
      color: theme.colors.textSecondary,
      textAlign: 'center',
      lineHeight: fs(24),
      marginBottom: v(28),
      maxWidth: s(360),
    },
    webButtonContainer: {
      width: '100%',
      alignItems: 'center',
      gap: v(12),
      marginBottom: v(32),
    },
    webPrimaryButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s(8),
      backgroundColor: theme.colors.primary,
      paddingVertical: v(14),
      paddingHorizontal: s(32),
      borderRadius: s(12),
      width: '100%',
      maxWidth: s(280),
    },
    webPrimaryButtonText: {
      fontSize: fs(16),
      fontFamily: GTFonts.semibold,
      color: theme.colors.textInverse,
    },
    webSecondaryButton: {
      paddingVertical: v(12),
      paddingHorizontal: s(24),
    },
    webSecondaryButtonText: {
      fontSize: fs(15),
      color: theme.colors.primary,
    },
    webButtonDisabled: {
      opacity: 0.6,
    },
    webTextDisabled: {
      opacity: 0.5,
    },
    webTipsContainer: {
      width: '100%',
      backgroundColor: theme.colors.surfaceSecondary,
      borderRadius: s(12),
      padding: s(20),
      gap: v(14),
    },
    webTipItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
    },
    webTipText: {
      fontSize: fs(14),
      fontFamily: LatoFonts.regular,
      color: theme.colors.textSecondary,
      flex: 1,
      lineHeight: fs(20),
    },
  });

export default NoInternetScreen;
